'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Check, Bookmark, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/lib/auth/context'
import { stashPendingCalc, type PendingCalc } from '@/lib/pending-calc'
import { CALCULO_FORMULA_VERSION } from '@/lib/calculos/version'
import {
  trackCalculadoraSalva,
  trackPremiumCtaClicado,
  type CalculatorType,
} from '@/lib/analytics'
import { PLANS } from '@/lib/billing/plans'
import { PremiumShowcase } from './premium-showcase'
import { cn } from '@/lib/utils'

interface SalvarCalculoCtaProps {
  show?: boolean
  source?: string
  className?: string
  hook?: string
  calcData?: PendingCalc
  title?: string
  subtitle?: string
  alertType?: string
  buttonLabel?: string
  context?: Record<string, unknown>
}

/**
 * Evento pós-cálculo: convite Premium com AMOSTRA VIVA (PremiumShowcase).
 * Vem depois do botão de exportar — vitrine, não pedágio.
 * Premium logado: atalho pra salvar no histórico.
 */
export function SalvarCalculoCta({
  show = true,
  source = 'calculadora',
  className,
  hook = 'Cálculo pronto. Quer o trunfo do CalculaMEI?',
  calcData,
}: SalvarCalculoCtaProps) {
  const { user, isLoading } = useAuth()
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const isPremium = user?.plano === 'PREMIUM' || user?.role === 'ADMIN'

  useEffect(() => {
    if (!show || isLoading || isPremium || !calcData) return
    try {
      stashPendingCalc(calcData)
    } catch {
      /* ignore */
    }
  }, [show, isLoading, isPremium, calcData])

  if (!show || isLoading) return null

  async function salvarAgora() {
    if (!calcData || saving) return
    setSaving(true)
    try {
      const res = await fetch('/api/calculos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...calcData, formulaVersion: CALCULO_FORMULA_VERSION }),
      })
      const d = await res.json().catch(() => null)
      if (res.ok && d?.success) {
        setSaved(true)
        const tipoMap: Record<string, CalculatorType> = {
          MARGEM_LUCRO: 'margem_lucro',
          PRECO_HORA: 'preco_hora',
          PRECIFICACAO: 'precificacao',
          FATURAMENTO: 'faturamento',
          FLUXO_CAIXA: 'fluxo_caixa',
          CALENDARIO_DAS: 'calendario_das',
        }
        const gaTipo = calcData?.tipo ? tipoMap[calcData.tipo] : undefined
        if (gaTipo) trackCalculadoraSalva(gaTipo)
        toast.success('Cálculo salvo no seu histórico!')
      } else {
        toast.error(d?.message || 'Não consegui salvar. Tente de novo.')
      }
    } catch {
      toast.error('Não consegui salvar. Tente de novo.')
    } finally {
      setSaving(false)
    }
  }

  if (user && isPremium) {
    return (
      <div className={cn('mt-5 border border-line bg-surface px-4 py-3.5', className)}>
        {calcData ? (
          <>
            <p className="text-[13.5px] text-ink-mute">
              <span className="font-semibold text-ink">Guarde este cálculo</span> pra comparar mês a mês.
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={salvarAgora}
                disabled={saving || saved}
                className="inline-flex items-center gap-1.5 rounded bg-brand px-4 py-2 text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : saved ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Bookmark className="h-3.5 w-3.5" />
                )}
                {saved ? 'Salvo!' : 'Salvar no meu histórico'}
              </button>
              <Link
                href={saved ? '/dashboard/historico' : '/dashboard'}
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-brand hover:underline"
              >
                {saved ? 'Ver meus cálculos' : 'Abrir meu painel'} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </>
        ) : (
          <Link
            href="/dashboard"
            className="inline-flex items-center gap-1.5 text-[13.5px] font-bold text-brand hover:underline"
          >
            Abrir meu painel <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    )
  }

  const ctaHref = user
    ? `/premium?motivo=pos_calculo&from=${encodeURIComponent(source)}`
    : `/sign-up?next=${encodeURIComponent(`/premium?motivo=pos_calculo&from=${source}`)}`

  const price = PLANS.PREMIUM.priceDisplay

  /**
   * Teaser Premium pós-cálculo — BREVE e sofisticado, por decisão de produto.
   * Vem DEPOIS do botão de exportar (nunca bloqueia a ação principal): card
   * claro com um filete de marca, os 3 recursos como chips e um link discreto —
   * sem botão gritante nem preview pesado. Dá visibilidade ao Premium sem
   * espantar quem só queria o resultado.
   */
  return (
    <section
      className={cn('mt-5 overflow-hidden rounded border border-line bg-surface', className)}
      aria-label="Conhecer o Premium"
    >
      <div className="border-l-2 border-brand p-4">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand">
            Premium · {price}/mês
          </p>
          <span className="hidden font-mono text-[10px] uppercase tracking-wide text-ink-faint sm:inline">
            sem anúncios
          </span>
        </div>

        <p className="mt-1.5 text-[14px] font-semibold leading-snug text-ink">{hook}</p>

        {/* Amostra viva das telas do Premium (auto-roda, pausa ao interagir) */}
        <PremiumShowcase />

        <div className="mt-3.5 flex items-center gap-4">
          <Link
            href={ctaHref}
            onClick={() => trackPremiumCtaClicado('pos_calculo')}
            className="inline-flex items-center gap-1.5 rounded bg-brand px-4 py-2 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
          >
            Conhecer o Premium <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <span className="text-[11px] text-ink-faint">cancele quando quiser</span>
        </div>
      </div>
    </section>
  )
}
